import React, { useState } from "react";
import {
	TextField,
	Button,
	Paper,
	Typography,
	CircularProgress,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	CssBaseline,
} from "@mui/material";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useNavigate, Link } from "react-router-dom";
import axios from "../../api/axios";
import { jwtDecode } from "jwt-decode";
import useAuth from "../../hooks/useAuth";
import { GrUserAdmin } from "react-icons/gr";
import mainAxios from "axios";

// Define styled Paper component
const StyledPaper = styled(Paper)(({ theme }) => ({
	padding: theme.spacing(4),
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	maxWidth: 400,
	margin: "auto",
	borderRadius: "20px",
	boxShadow: theme.shadows[5],
	transition: "transform 0.2s",
	"&:hover": {
		transform: "scale(1.03)",
	},
}));

// Security questions options
const securityQuestions = [
	"What is your mother's maiden name?",
	"What was your first pet's name?",
	"What was the name of your elementary school?",
	"What is your favorite color?",
];

const AdminRegisterForm = () => {
	const navigate = useNavigate();
	const { setAuth } = useAuth();

	// Form states
	const [fullName, setFullName] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [securityQuestion, setSecurityQuestion] = useState("");
	const [securityAnswer, setSecurityAnswer] = useState("");
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const [successMsg, setSuccessMsg] = useState("");

	// Handle form submission
	const handleRegister = async (e) => {
		e.preventDefault();
		setError("");
		setSuccessMsg("");

		// Basic validation
		if (
			!fullName ||
			!email ||
			!password ||
			!securityQuestion ||
			!securityAnswer
		) {
			setError("Please fill in all required fields.");
			return;
		}

		if (!validateEmail(email)) {
			setError("Please enter a valid email address.");
			return;
		}

		setLoading(true);
		try {
			console.log(fullName, email, password, securityAnswer, securityQuestion);
			const response = await axios.post("/admin-secured-register", {
				fullName,
				email,
				password,
				securityQuestion,
				securityAnswer,
			});

			if (response.status === 200 || response.status === 201) {
				setSuccessMsg("Registration successful! Redirecting to login...");
				// Redirect or perform other actions
				setTimeout(() => {
					navigate("/DTCL-secured-with-key-admin-login");
				}, 1500);
			}
		} catch (err) {
			if (mainAxios.isAxiosError(err)) {
				if (err.response && err.response.data) {
					setError(err.response.data.message || "Registration failed");
				} else {
					setError(err.message);
				}
			} else {
				setError("An unexpected error occurred");
			}
		} finally {
			setLoading(false);
		}
	};

	const validateEmail = (email) => {
		const re = /\S+@\S+\.\S+/;
		return re.test(email);
	};

	return (
		<motion.div
			initial={{ opacity: 0, y: -30 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.5 }}
			className="      w-[90%]   my-[3rem]   mx-auto">
			<StyledPaper elevation={3}>
				<Typography variant="h4" sx={{ fontWeight: "bold" }} gutterBottom>
					DTCL Admin Registration
				</Typography>
				<Typography sx={{ color: "gray", fontSize: "0.9rem", mb: 2 }}>
					<GrUserAdmin className="inline-block mr-[0.5rem]" />
					Register a new admin account.
				</Typography>
				<form onSubmit={handleRegister} style={{ width: "100%" }} noValidate>
					<TextField
						label="Full Name"
						variant="outlined"
						fullWidth
						margin="normal"
						required
						value={fullName}
						onChange={(e) => setFullName(e.target.value)}
					/>
					<TextField
						label="Email"
						variant="outlined"
						fullWidth
						margin="normal"
						required
						value={email}
						onChange={(e) => setEmail(e.target.value)}
					/>
					<TextField
						label="Password"
						type="password"
						variant="outlined"
						fullWidth
						margin="normal"
						required
						value={password}
						onChange={(e) => setPassword(e.target.value)}
					/>
					{/* Security Question Dropdown */}
					<FormControl fullWidth margin="normal" required>
						<InputLabel id="security-question-label">
							Security Question
						</InputLabel>
						<Select
							labelId="security-question-label"
							value={securityQuestion}
							onChange={(e) => setSecurityQuestion(e.target.value)}
							label="Security Question">
							{securityQuestions.map((question, index) => (
								<MenuItem key={index} value={question}>
									{question}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					{/* Security Answer Dropdown */}
					{/* <FormControl fullWidth margin="normal" required>
						<InputLabel id="security-answer-label">Security Answer</InputLabel>
						<Select
							labelId="security-answer-label"
							value={securityAnswer}
							onChange={(e) => setSecurityAnswer(e.target.value)}
							label="Security Answer">
							<MenuItem value="Option1">Option 1</MenuItem>
							<MenuItem value="Option2">Option 2</MenuItem>
							<MenuItem value="Option3">Option 3</MenuItem>
						</Select>
					</FormControl> */}
					<TextField
						label="Secret answer"
						variant="outlined"
						fullWidth
						margin="normal"
						required
						value={securityAnswer}
						onChange={(e) => setSecurityAnswer(e.target.value)}
					/>
					<Button
						type="submit"
						variant="contained"
						color="primary"
						fullWidth
						disabled={loading}
						sx={{ position: "relative" }}>
						{loading && (
							<CircularProgress
								size={24}
								sx={{
									position: "absolute",
									top: "50%",
									left: "50%",
									transform: "translate(-50%, -50%)",
								}}
							/>
						)}
						{!loading && "Register"}
					</Button>
					{error && (
						<Typography color="error" align="center" sx={{ mt: 2 }}>
							{error}
						</Typography>
					)}
					{successMsg && (
						<Typography color="primary" align="center" sx={{ mt: 2 }}>
							{successMsg}
						</Typography>
					)}
				</form>
				<Typography variant="body2" align="center" sx={{ mt: 2 }}>
					Already have an account?{" "}
					<Link to="/en/login" className="text-blue-500">
						Login
					</Link>
				</Typography>
			</StyledPaper>
		</motion.div>
	);
};

const theme = createTheme({
	palette: {
		primary: { main: "#1976d2" },
		secondary: { main: "#dc004e" },
	},
});

export default function AdminRegForm() {
	return (
		<ThemeProvider theme={theme}>
			<CssBaseline />
			<section
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					minHeight: "100vh",
					backgroundColor: "#f5f5f5",
				}}>
				<AdminRegisterForm />
			</section>
		</ThemeProvider>
	);
}
